import { ResultsView } from '@kijiji/generated/graphql-types'
import { formatWholeNumber } from '@kijiji/number/formatWholeNumber'
import { useTranslation } from 'next-i18next'
import { useTheme } from 'styled-components'

import { HideAtOrLarger, ShowAtOrLarger } from '@/components/shared/breakpoint'
import { getListingCounts } from '@/components/srp/results-sort/results/getListingCounts'
import { SRP_LISTING_LIMIT } from '@/constants/pageSettings'
import { ROUTE_LOCALE } from '@/domain/locale'
import { useGetSrpState } from '@/features/search/hooks/useGetSrpState'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { useSrpMwebRedesign } from '@/hooks/srp/useSrpMwebRedesign'
import { useLocale } from '@/hooks/useLocale'
import { type BodyTextProps, BodyText } from '@/ui/atoms/body-text'
import { VisuallyHidden } from '@/ui/atoms/visually-hidden'

export const SrpResults = () => {
  const { data } = useGetSearchResultsData()
  const { viewType, currentPage } = useGetSrpState()

  const { t } = useTranslation('srp')
  const { colors } = useTheme()
  const { routeLocale } = useLocale()

  const isSrpControlsMwebToggleEnabled = useSrpMwebRedesign()

  const { totalCount: totalListings = 0, limit = SRP_LISTING_LIMIT } = data?.pagination || {}

  const listingCounts = getListingCounts(totalListings, currentPage, limit)
  const isMapView = viewType === ResultsView.Map

  const listingCountTranslationParam = {
    ...listingCounts.params,
    total: formatWholeNumber(totalListings, routeLocale === ROUTE_LOCALE.en),
  }

  if (isMapView) {
    return (
      <VisuallyHidden>
        <p>{t(listingCounts.intl, listingCountTranslationParam)}</p>
      </VisuallyHidden>
    )
  }

  const resultTextProps: BodyTextProps = {
    ['aria-live']: 'polite',
    as: 'h2',
    color: colors.grey.primary,
    size: { small: 'small', medium: 'medium' },
    weight: 'bold',
  }

  const originalResultsTextComponent = (
    <BodyText data-testid="srp-results" {...resultTextProps}>
      {t(listingCounts.intl, listingCountTranslationParam)}
    </BodyText>
  )

  if (isSrpControlsMwebToggleEnabled) {
    return (
      <>
        <ShowAtOrLarger breakpoint="medium">{originalResultsTextComponent}</ShowAtOrLarger>

        <HideAtOrLarger breakpoint="medium">
          <BodyText
            data-testid="srp-results"
            textAlign="center"
            weight="medium"
            {...resultTextProps}
          >
            {t('srp:header.results.total_count', listingCountTranslationParam)}
          </BodyText>
        </HideAtOrLarger>
      </>
    )
  }

  return isMapView ? (
    originalResultsTextComponent
  ) : (
    <ShowAtOrLarger breakpoint="medium">{originalResultsTextComponent}</ShowAtOrLarger>
  )
}
